import {
  useActiveOrganization,
  useOrganizations,
  useSetActiveOrganization,
} from '@repo/api-client/react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { BusinessAvatar, brandingColor } from '@/features/avatar';
import { cn } from '@/lib/utils';

/**
 * Persistent active-organization picker that lives at the top of the
 * sidebar. The session keeps its own copy of the org list (separate
 * from the React Query cache) so we can render this before any
 * other query subscribes — see SessionProvider#syncOrganizations.
 */
export function OrgSwitcher() {
  const orgs = useOrganizations();
  const active = useActiveOrganization();
  const setActive = useSetActiveOrganization();
  const navigate = useNavigate();

  if (orgs.length === 0) {
    return (
      <Skeleton className="h-12 w-full rounded-lg" data-testid="org-switcher-loading" />
    );
  }

  const display = active ?? orgs[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          className={cn(
            'h-12 w-full justify-between gap-2 rounded-lg border-line bg-card px-3 text-left text-sm font-medium',
            'hover:bg-secondary/40',
          )}
          aria-label="Switch business"
        >
          <div className="flex min-w-0 items-center gap-2">
            {display ? (
              <BusinessAvatar
                id={display.id}
                name={display.name}
                initials={display.nickname ? display.nickname.slice(0, 3).toUpperCase() : undefined}
                color={brandingColor({ id: display.id, branding: display.branding })}
                logoUrl={display.logoUrl ?? null}
                size={28}
                radius={6}
                fontSize={11}
              />
            ) : null}
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-semibold leading-tight">
                {display?.nickname ?? display?.name}
              </span>
              <span className="truncate text-[11px] font-normal text-muted-foreground">
                {display?.currency} · {display?.role}
              </span>
            </span>
          </div>
          <ChevronsUpDown className="size-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>Your businesses</DropdownMenuLabel>
        {orgs.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onSelect={() => setActive(org)}
            className="flex items-center justify-between gap-2"
          >
            <span className="flex min-w-0 items-center gap-2">
              <BusinessAvatar
                id={org.id}
                name={org.name}
                initials={org.nickname ? org.nickname.slice(0, 3).toUpperCase() : undefined}
                color={brandingColor({ id: org.id, branding: org.branding })}
                logoUrl={org.logoUrl ?? null}
                size={28}
                radius={6}
                fontSize={11}
              />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm">{org.nickname || org.name}</span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {org.currency} · {org.role}
                </span>
              </span>
            </span>
            {active?.id === org.id ? <Check className="size-4 text-accent" /> : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings/businesses/new')}>
          <Plus className="size-4" /> New business
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings/businesses')}>
          Manage businesses
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
